import * as React from "react";
import { cn } from "../../lib/cn";

export interface SurfaceHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Small uppercase label above the title (e.g. "Calendario", "Documenti"). */
  label?: React.ReactNode;
  /** Big surface title (e.g. "12 — 18 Maggio"). */
  title: React.ReactNode;
  /**
   * Optional meta strip rendered under the title — accepts arbitrary content
   * so each surface can mix bold counts and accent text (e.g.
   * `<><strong>8 eventi</strong> · prossimo tra 25 min</>`).
   */
  meta?: React.ReactNode;
  /** Right-aligned actions slot — view-mode segmented control + "+ Nuovo". */
  actions?: React.ReactNode;
}

/**
 * SurfaceHeader — DRY title block reused by every workspace surface.
 *
 * Matches the v18 mockup `.surface-header` layout: label/title/meta on the
 * left, free-form actions on the right. The component is intentionally
 * presentation-only — wiring (filters, view mode, primary CTA handler)
 * lives in the consuming Hub.
 */
export const SurfaceHeader = React.forwardRef<HTMLDivElement, SurfaceHeaderProps>(
  ({ label, title, meta, actions, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Jul 2026 — compact density (padding + title trimmed from pt-7/pb-6 +
        // 26px) to reclaim ~34px of vertical chrome on EVERY hub. Horizontal
        // px-8 stays airy (pencil). A hub needing more room overrides via
        // ``className`` — tailwind-merge lets the later value win.
        "flex flex-row items-end justify-between gap-4 border-black/5 border-b px-8 pt-4 pb-3",
        className,
      )}
      {...rest}
    >
      <div className="min-w-0">
        {label ? (
          <div className="mb-0.5 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
            {label}
          </div>
        ) : null}
        <h1 className="font-[550] font-display text-[20px] text-ink-1 leading-tight tracking-[-0.022em]">
          {title}
        </h1>
        {meta ? (
          <div className="mt-1 text-[12px] text-ink-3 tabular-nums [&_strong]:font-medium [&_strong]:text-ink-1">
            {meta}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
      ) : null}
    </div>
  ),
);
SurfaceHeader.displayName = "SurfaceHeader";
